import type { CardProps } from '@chakra-ui/react'
import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { format } from 'date-fns'
import { forwardRef, useState } from 'react'

import { useAuth } from '@/context/AuthContext'
import { DashboardCard } from '../DashboardCard'
import { ChartsGrid } from './ChartsGrid'
import { CountStatsGrid } from './CountStatsGrid'
import { InsuranceCompaniesSection } from './InsuranceCompaniesSection'
import { MainStatsGrid } from './MainStatsGrid'
import { StatsOptionsControls } from './StatsOptionsControls'
import { useStatsData } from './useStatsData'

export type StatsProps = CardProps

export const Stats = forwardRef<HTMLDivElement, StatsProps>((props, ref) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [ouId, setOuId] = useState<number | undefined>()
  const [userId, setUserId] = useState<string | undefined>()
  const { user } = useAuth()

  const showCategories = true
  const showOrganizationUserControls = user?.allowDisplayOrganization

  const {
    stats,
    adjustedPreviousStats,
    isLoadingStats,
    anyData,
    timeRange,
    previousTimeRangeLabel,
    adjustedPreviousTimeRangeLabel,
    reload
  } = useStatsData(ouId, userId, showCategories)

  const toggleSettings = () => {
    setIsSettingsOpen((prev) => !prev)
  }

  const handleOrganizationUserChange = (newOuId?: number, newUserId?: string) => {
    setOuId(newOuId)
    setUserId(newUserId)
  }

  return (
    <DashboardCard
      title={
        <Flex justifyContent='space-between' alignItems='center'>
          <Text>Statistiky</Text>
          <Text fontSize='sm' fontWeight='normal'>
            {format(timeRange.timeRange.dateFrom, 'd. MMMM yyyy')} -{' '}
            {format(timeRange.timeRange.dateTo, 'd. MMMM yyyy')}
          </Text>
        </Flex>
      }
      ref={ref}
      {...props}
    >
      <Grid sx={{
        gridTemplateColumns: { base: '100%', lg: '3fr 2fr' },
        rowGap: 6,
        columnGap: { base: 6, xl: 12 },
        px: { base: 4, md: 6 }
      }}>
        <Grid rowGap={6} gridTemplateColumns='1fr' alignContent='start'>
          <Grid sx={{ gap: 6, gridTemplateColumns: { lg: '1fr 2fr' } }} alignItems='start'>
            <MainStatsGrid
              stats={stats}
              adjustedPreviousStats={adjustedPreviousStats}
              isLoadingStats={isLoadingStats}
              adjustedPreviousTimeRangeLabel={adjustedPreviousTimeRangeLabel}
              previousTimeRangeLabel={previousTimeRangeLabel}
            />

            <ChartsGrid
              stats={stats}
              adjustedPreviousStats={adjustedPreviousStats}
              isLoadingStats={isLoadingStats}
              anyData={anyData}
              previousTimeRangeLabel={previousTimeRangeLabel}
              showCategories={showCategories}
            />
          </Grid>

          <CountStatsGrid stats={stats} isLoadingStats={isLoadingStats} />
        </Grid>

        <InsuranceCompaniesSection stats={stats} isLoadingStats={isLoadingStats} />
      </Grid>

      <Box sx={{
        px: 4,
        py: 3,
        mt: { base: 4, md: 8 },
        bg: 'neutral.50',
        borderBottomRadius: 'md',
      }}>
        <StatsOptionsControls
          {...timeRange}
          isSettingsOpen={isSettingsOpen}
          toggleSettings={toggleSettings}
          reload={reload}
          isLoadingStats={isLoadingStats}
          ouId={ouId}
          userId={userId}
          onOrganizationUserChange={handleOrganizationUserChange}
          showOrganizationUserControls={showOrganizationUserControls}
        />
      </Box>
    </DashboardCard>
  )
})